import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import CircuitBackground from "./CircuitBackground";
import CompanyNameColorLogo from "./CompanyNameColorLogo";
import EnvironmentBadge from "./EnvironmentBadge";

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  welcomeTitle: string;
  /** Optional: full-page background image (e.g. "/login-bg.jpg"). When set with darkTheme, covers entire viewport. */
  leftBackgroundImage?: string;
  /** If true, right panel and card use dark theme (for login reference design). */
  darkTheme?: boolean;
  /** Optional: e.g. "/login-white-logo.png" — when set with darkTheme, show this logo instead of color logo. */
  logoImage?: string;
  /** Optional: tagline under logo (e.g. "QUALITY. PRODUCTIVITY. COMPOUNDED."). Used when logoImage is set. */
  tagline?: string;
  /** Optional: content rendered below the login card (e.g. "Don't have an account? Signup"). */
  belowCardContent?: React.ReactNode;
}

// Custom hook to get viewport height
const useViewportHeight = () => {
  const [viewportHeight, setViewportHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => {
      setViewportHeight(window.innerHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return viewportHeight;
};

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  title,
  welcomeTitle,
  leftBackgroundImage,
  darkTheme = false,
  logoImage,
  belowCardContent,
}) => {
  const viewportHeight = useViewportHeight();
  const useDarkTheme = darkTheme;
  const fullPageBg = useDarkTheme && leftBackgroundImage;

  const leftPanel = !fullPageBg ? (
    <div
      className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center bg-no-repeat"
      style={
        leftBackgroundImage
          ? { backgroundImage: `url(${leftBackgroundImage})` }
          : { background: "#153C7F" }
      }
    >
      {!leftBackgroundImage && <CircuitBackground />}
      {!leftBackgroundImage && (
        <div className="relative z-20 flex flex-col justify-end px-10 pb-5 text-white h-full">
          <div className="max-w-lg">
            <h1 className="text-6xl font-extrabold mb-6 leading-tight">
              {welcomeTitle}
            </h1>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const contentArea = (
    <div
      className={`flex flex-col flex-1 items-center w-full max-w-full ${
        fullPageBg ? "min-w-0 px-0 sm:px-2" : "p-6 sm:p-8 lg:p-10"
      }`}
    >
      {/* LOGO */}
      <div
        className={`w-full mb-auto ${
          fullPageBg ? "text-center" : "max-w-md mx-auto"
        }`}
      >
        {useDarkTheme && logoImage ? (
          <Link
            href="/"
            className={`block relative mb-2 h-20 sm:h-24 md:h-32 ${
              fullPageBg
                ? "max-w-[260px] sm:max-w-[350px] mx-auto"
                : "w-full max-w-[350px] "
            }`}
          >
            <Image
              src={logoImage}
              alt="CareAscendium"
              fill
              className={`object-contain ${
                fullPageBg ? "object-center" : "object-left"
              }`}
            />
          </Link>
        ) : (
          <Link href="/" className="block">
            <CompanyNameColorLogo />
          </Link>
        )}
      </div>

      {/* MAIN */}
      <div className={`flex flex-col w-full justify-between items-center`}>
        {useDarkTheme && (
          <div
            className={`w-full mb-3 sm:mb-4 ${
              fullPageBg ? "text-center" : "max-w-md mx-auto"
            }`}
          >
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-1">
              {welcomeTitle}
            </h1>
          </div>
        )}

        <div
          className={`w-full relative ${fullPageBg ? "max-w-md" : ""} ${
            useDarkTheme
              ? "border-2 border-gray-300/80 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:px-8 lg:py-6"
              : "bg-white rounded-2xl p-6 sm:p-8"
          }`}
        >
          <div className="absolute top-5 right-5">
            <EnvironmentBadge />
          </div>
          {!useDarkTheme && (
            <h2 className="text-2xl font-semibold text-gray-500 mb-6 text-center mt-14">
              {title}
            </h2>
          )}
          {useDarkTheme && (
            <h2 className="text-lg sm:text-xl font-bold text-white mb-5 sm:mb-7 text-center">
              {title}
            </h2>
          )}
          {children}
        </div>

        {belowCardContent && (
          <div className="w-full mt-2 flex justify-center">
            {belowCardContent}
          </div>
        )}
      </div>

      {/* C */}
      <div
        className={`w-full mt-auto text-center text-xs sm:text-sm ${
          useDarkTheme ? "text-slate-200" : "text-gray-600"
        }`}
      >
        © CareAscendium, LLC. | {new Date().getFullYear()}{" "}
        <Link
          href="/privacy-policy"
          className={
            useDarkTheme
              ? "text-sky-300/90 hover:text-sky-200 underline"
              : "text-gray-500 hover:text-[#153C7F] underline"
          }
        >
          Privacy Policy
        </Link>
        {" | "}
        <Link
          href="/terms-and-conditions"
          className={
            useDarkTheme
              ? "text-sky-300/90 hover:text-sky-200 underline"
              : "text-gray-500 hover:text-[#153C7F] underline"
          }
        >
          Terms & Conditions
        </Link>
        {" | "}
        <Link
          href="/cookie-policy"
          className={
            useDarkTheme
              ? "text-sky-300/90 hover:text-sky-200 underline"
              : "text-gray-500 hover:text-[#153C7F] underline"
          }
        >
          Cookie Policy
        </Link>
      </div>
    </div>
  );

  if (fullPageBg) {
    return (
      <div
        className="relative min-h-screen w-full flex flex-col md:flex-row"
        style={{
          minHeight: viewportHeight > 0 ? `${viewportHeight}px` : "100dvh",
        }}
      >
        {/* Background in document flow so it scales with zoom like the rest of the page */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundColor: "#0f172a",
            backgroundImage: `url(${leftBackgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "scroll",
          }}
          aria-hidden
        />
        {/* Left 50%: spacer — hidden on small screens */}
        <div
          className="hidden md:block md:w-1/2 min-h-full flex-shrink-0 relative z-[1]"
          style={{
            minHeight: viewportHeight > 0 ? `${viewportHeight}px` : "100dvh",
          }}
          aria-hidden
        />
        {/* Right: full width on mobile, 50% from md up */}
        <div
          className="relative z-10 w-full md:w-1/2 flex flex-col items-center justify-between px-4 sm:px-6 lg:px-8 py-6 overflow-auto min-h-full"
          style={{
            minHeight: viewportHeight > 0 ? `${viewportHeight}px` : "100dvh",
          }}
        >
          {contentArea}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#f7f7f7]">
      {leftPanel}
      {contentArea}
    </div>
  );
};

export default AuthLayout;
